import {
  DATE_CONSTANTS,
  DateRange,
  DynamicValue,
  ExcludeCurrentUnit,
  RelativeDateOperator,
  ThisTimeUnit,
  TimeUnit,
  calculateDateRange,
  canExcludeCurrent,
  createDynamicValue,
} from '@doss/common-all'
import { CalendarIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getBaseInputClasses } from '@/lib/style-utils'
import { cn } from '@/lib/utils'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { Selector } from '~/@/components/selector'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import './dynamic-date-range-picker.css'

const TIME_UNIT_SINGULAR: Record<TimeUnit, 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'> =
  {
    minutes: 'minute',
    hours: 'hour',
    days: 'day',
    weeks: 'week',
    months: 'month',
    years: 'year',
  } as const

// Time units that require DateTime (not available for Date-only columns)
const DATE_TIME_ONLY_UNITS: TimeUnit[] = ['minutes', 'hours']

export type SingularTimeUnit = (typeof TIME_UNIT_SINGULAR)[keyof typeof TIME_UNIT_SINGULAR]

export type DynamicRangePickerProps = {
  operator: RelativeDateOperator
  disabled?: boolean
  value?: DynamicValue
  onChange: (value: DynamicValue, calculatedRange: DateRange) => void
  className?: string
  /** When true, shows minute/hour options in addition to day/week/month/year */
  isDateTime?: boolean
}

export function DynamicDateRangePicker({
  operator,
  disabled,
  onChange,
  value,
  className,
  isDateTime,
}: DynamicRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false)

  const { t } = useTypedTranslation()
  const { user } = useGlobalContext()
  // Excluding the current week uses the org's start of week, as the filter itself does
  const startOfWeekDay = user?.organizationStartOfWeekDay
  const rangeOptions = useMemo(() => ({ startOfWeekDay }), [startOfWeekDay])
  // Local state for tracking inputs only
  const [localValue, setLocalValue] = useState<number | undefined>(
    value?.type === 'relative' ? value.value : undefined,
  )
  const [localTimeUnit, setLocalTimeUnit] = useState<TimeUnit | undefined>(
    value?.type === 'relative' ? value.unit : undefined,
  )
  const [localThisUnit, setLocalThisUnit] = useState<ThisTimeUnit | undefined>(
    value?.type === 'this' ? value.unit : undefined,
  )
  // Whether to exclude the current period, and which one. The unit is kept while the box is unticked.
  const [localIsExcludingCurrent, setLocalIsExcludingCurrent] = useState<boolean>(
    value?.type === 'relative' ? value.excludeCurrent !== undefined : false,
  )
  const [localExcludeCurrentUnit, setLocalExcludeCurrentUnit] = useState<ExcludeCurrentUnit>(
    value?.type === 'relative' ? (value.excludeCurrent ?? 'day') : 'day',
  )

  useEffect(() => {
    if (operator === 'this') {
      // When switching to 'this', clear relative date values
      setLocalValue(undefined)
      setLocalTimeUnit(undefined)
    } else {
      // When switching to any other operator, clear 'this' values
      setLocalThisUnit(undefined)
      // If switching between other relative operators while the time unit and value are defined,
      // apply changes immediately upon operator change so that the date picker shows the correct dates selected when the user opens it
      if (localTimeUnit && localValue) {
        applyChanges()
      }
    }
  }, [operator])

  // Update local state when value prop changes
  useEffect(() => {
    if (value?.type === 'relative') {
      setLocalValue(value.value)
      setLocalTimeUnit(value.unit)
      setLocalThisUnit(undefined)
      // Hour and minute ranges never carry excludeCurrent, so keep the user's choice through them
      if (canExcludeCurrent(value.operator, value.unit)) {
        setLocalIsExcludingCurrent(value.excludeCurrent !== undefined)
      }
      if (value.excludeCurrent) {
        setLocalExcludeCurrentUnit(value.excludeCurrent)
      }
    } else if (value?.type === 'this') {
      setLocalValue(undefined)
      setLocalTimeUnit(undefined)
      setLocalThisUnit(value.unit)
    }
  }, [value])

  // Function to create and emit changes
  const applyChanges = useCallback(
    (
      newLocalValue?: number,
      newLocalTimeUnit?: TimeUnit,
      newLocalThisUnit?: ThisTimeUnit,
      newLocalExclude?: { isExcludingCurrent?: boolean; unit?: ExcludeCurrentUnit },
    ) => {
      const valueToUse = newLocalValue ?? localValue
      const timeUnitToUse = newLocalTimeUnit ?? localTimeUnit
      const thisUnitToUse = newLocalThisUnit ?? localThisUnit
      const isExcludingCurrent = newLocalExclude?.isExcludingCurrent ?? localIsExcludingCurrent
      const excludeCurrentUnit = newLocalExclude?.unit ?? localExcludeCurrentUnit

      if (operator === 'this' && thisUnitToUse) {
        const newValue = createDynamicValue.this(thisUnitToUse)
        const range = calculateDateRange(newValue, rangeOptions)
        onChange(newValue, range)
      } else if (operator !== 'this' && valueToUse && timeUnitToUse) {
        const relativeOperator = operator as Exclude<RelativeDateOperator, 'this'>
        const newValue = createDynamicValue.relative(
          relativeOperator,
          valueToUse,
          timeUnitToUse,
          // Dropped for operators and units it doesn't apply to, such as hours
          isExcludingCurrent && canExcludeCurrent(relativeOperator, timeUnitToUse)
            ? excludeCurrentUnit
            : undefined,
        )
        const range = calculateDateRange(newValue, rangeOptions)
        onChange(newValue, range)
      }
    },
    [
      operator,
      localValue,
      localTimeUnit,
      localThisUnit,
      localIsExcludingCurrent,
      localExcludeCurrentUnit,
      rangeOptions,
      onChange,
    ],
  )

  const handleClose = useCallback(() => {
    // If the user cleared the numeric input for a relative range, reset the value
    // to the original value
    if (value?.type === 'relative' && localValue === undefined) {
      setLocalValue(value?.value)
    }
    applyChanges()
    setIsOpen(false)
  }, [value, localValue, applyChanges])

  const formattedValue = useMemo(() => {
    if (operator === 'this' && localThisUnit) {
      // This unit is singular always
      return t(`date_picker.${localThisUnit}`, { count: 1 })
    } else if (operator !== 'this' && localValue && localTimeUnit) {
      const singularUnit = TIME_UNIT_SINGULAR[localTimeUnit]
      const range = t(`date_picker.${singularUnit}_with_count`, {
        count: localValue,
      })
      if (localIsExcludingCurrent && canExcludeCurrent(operator, localTimeUnit)) {
        return t(`date_picker.excluding_${localExcludeCurrentUnit}_summary`, { range })
      }
      return range
    }
    return ''
  }, [
    operator,
    localValue,
    localTimeUnit,
    localThisUnit,
    localIsExcludingCurrent,
    localExcludeCurrentUnit,
    t,
  ])

  const excludeCurrentOptions = useMemo(
    () =>
      DATE_CONSTANTS.UNITS.EXCLUDE_CURRENT.map((unit) => ({
        value: unit,
        label: t(`date_picker.${unit}`, { count: 1 }),
      })),
    [t],
  )

  const relativeRangeDropdownOptions = useMemo(() => {
    // Filter out minute/hour options for Date-only columns
    const availableUnits = isDateTime
      ? DATE_CONSTANTS.UNITS.TIME
      : DATE_CONSTANTS.UNITS.TIME.filter((unit) => !DATE_TIME_ONLY_UNITS.includes(unit))

    return availableUnits.map((unit) => ({
      value: unit,
      label: t(`date_picker.${TIME_UNIT_SINGULAR[unit]}`, { count: localValue || 2 }), // Default to plural if no value is set
    }))
  }, [localValue, isDateTime])

  const selectedRange = useMemo(
    () => calculateDateRange(value, rangeOptions),
    [value, rangeOptions],
  )
  const selectedDateRange = useMemo(() => {
    return {
      from: selectedRange.from?.toDate(),
      to: selectedRange.to?.toDate(),
    }
  }, [selectedRange])

  const handleInputChange = useCallback(
    (newValue: number | undefined) => {
      // Don't handle input changes if we're in 'this' mode
      if (operator === 'this') {
        return
      }

      setLocalValue(newValue)
      if (localTimeUnit && newValue !== undefined) {
        // Only call applyChanges if we have both values
        applyChanges(newValue)
      }
    },
    [operator, localTimeUnit, applyChanges],
  )

  const handleExcludeCurrentChange = useCallback(
    (checked: boolean) => {
      setLocalIsExcludingCurrent(checked)
      applyChanges(undefined, undefined, undefined, { isExcludingCurrent: checked })
    },
    [applyChanges],
  )

  const handleExcludeCurrentUnitChange = useCallback(
    (newUnit: string | null) => {
      if (!newUnit) {
        return
      }
      const unit = newUnit as ExcludeCurrentUnit
      setLocalExcludeCurrentUnit(unit)
      applyChanges(undefined, undefined, undefined, { unit })
    },
    [applyChanges],
  )

  const handleUnitChange = useCallback(
    (newUnit: string | null) => {
      if (!newUnit) return

      if (operator === 'this') {
        const thisUnit = newUnit as ThisTimeUnit
        setLocalThisUnit(thisUnit)
        applyChanges(undefined, undefined, thisUnit)
      } else {
        const timeUnit = newUnit as TimeUnit
        setLocalTimeUnit(timeUnit)
        applyChanges(undefined, timeUnit)
      }
    },
    [operator, applyChanges],
  )

  const input = useMemo(() => {
    if (operator === 'this') {
      return (
        <Selector
          onChange={handleUnitChange}
          options={[...DATE_CONSTANTS.UNITS.THIS]}
          placeholder={t('date_picker.select_unit')}
          triggerProps={{ variant: 'outline' }}
          value={localThisUnit}
        />
      )
    }

    if (operator === 'last' || operator === 'inTheNext') {
      const rangeInputs = (
        <div className="flex gap-2">
          <Input
            min={1}
            onChange={(e) => {
              const value = e.target.value === '' ? undefined : parseInt(e.target.value)

              handleInputChange(value)
            }}
            type="number"
            value={localValue ?? ''}
            variant="outline"
          />
          <Selector
            onChange={handleUnitChange}
            options={relativeRangeDropdownOptions}
            placeholder={t('date_picker.select_unit')}
            triggerProps={{
              variant: 'outline',
            }}
            value={localTimeUnit}
          />
        </div>
      )
      // Excluding the current period doesn't apply to hour and minute ranges
      if (!localTimeUnit || !canExcludeCurrent(operator, localTimeUnit)) {
        return rangeInputs
      }
      return (
        <div className="relative-range-inputs">
          {rangeInputs}
          <div className="exclude-current">
            <Checkbox
              checked={localIsExcludingCurrent}
              label={t('date_picker.exclude_current')}
              onCheckedChange={(checked) => handleExcludeCurrentChange(checked === true)}
            />
            <Selector
              disabled={!localIsExcludingCurrent}
              onChange={handleExcludeCurrentUnitChange}
              options={excludeCurrentOptions}
              triggerProps={{
                variant: 'outline',
              }}
              value={localExcludeCurrentUnit}
            />
          </div>
        </div>
      )
    }
    return null
  }, [
    operator,
    localValue,
    localTimeUnit,
    localThisUnit,
    localIsExcludingCurrent,
    localExcludeCurrentUnit,
    relativeRangeDropdownOptions,
    excludeCurrentOptions,
    handleInputChange,
    handleUnitChange,
    handleExcludeCurrentChange,
    handleExcludeCurrentUnitChange,
  ])

  return (
    <InputWrapper>
      <Popover
        onOpenChange={(isOpen: boolean) => (!isOpen ? handleClose() : setIsOpen(true))}
        open={isOpen}
      >
        <PopoverTrigger asChild>
          <Button
            className={cn(
              // Use base input classes for common styling
              getBaseInputClasses(disabled),
              // Override with button-specific styling
              'group rounded ps-3 text-left text-xs text-gray-11 justify-between active:scale-100 h-8 hover:border-gray-5 focus:hover:border-blue-8',
              isOpen &&
                'border-blue-8 shadow-input-focus hover:border-blue-8 hover:shadow-input-focus',
              !formattedValue && 'text-muted-foreground',
              className,
            )}
            disabled={disabled}
            variant="ghost"
          >
            <span className="flex-1">{formattedValue}</span>
            <CalendarIcon className="h-4 w-4 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="dynamic-date-range-picker-panel z-[1301] w-auto p-0"
        >
          <div className="flex flex-col gap-4 pt-5">
            <div className="px-5">{input}</div>
            <div className="flex flex-row items-start border-b border-t border-gray-4">
              <Calendar
                className="px-5"
                disabled={true}
                initialFocus
                mode="range"
                selected={selectedDateRange}
              />
            </div>
            <div className="flex justify-end gap-2 p-4 pt-0">
              <Button className="rounded" onClick={handleClose} size="xs" variant="primary">
                Done
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </InputWrapper>
  )
}
